import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

const NAVY_DARK = '#0E2238';
const GOLD = '#F5A623';
const GOLD_LIGHT = '#FBBF4A';

const navLinks = [
  { label: 'Home', path: '/' },
  { label: 'About Us', path: '/about' },
  { label: 'Services', path: '/services' },
  { label: 'Contact', path: '/contact' },
];

export default function Navbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Close menu on route change
  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  const linkStyle = (active) => ({
    color: active ? '#fff' : 'rgba(255,255,255,0.75)',
    fontSize: '14px', fontWeight: '500', textDecoration: 'none',
    letterSpacing: '0.3px', paddingBottom: '4px',
    borderBottom: active ? `2px solid ${GOLD}` : '2px solid transparent',
    transition: 'color 0.2s, border-color 0.2s',
  });

  return (
    <>
      <nav style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100,
        background: 'rgba(14,34,56,0.97)', backdropFilter: 'blur(12px)',
        borderBottom: '1px solid rgba(245,166,35,0.2)',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0 48px', height: '70px',
        boxShadow: scrolled ? '0 4px 24px rgba(0,0,0,0.3)' : 'none',
        transition: 'box-shadow 0.3s',
      }}>
        {/* BRAND */}
        <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: '12px', textDecoration: 'none' }}
          onClick={() => setMenuOpen(false)}>
          <img src="/logo.jpeg" alt="Ensign Electrotech Logo"
            style={{ width: '42px', height: '42px', borderRadius: '8px', objectFit: 'cover', flexShrink: 0 }} />
          <div>
            <div style={{ fontFamily: "'Playfair Display', serif", fontSize: '15px', fontWeight: 700, color: '#fff', lineHeight: 1.2 }}>
              Ensign Electrotech LLP
            </div>
            <div style={{ fontSize: '10px', fontWeight: 400, color: GOLD, letterSpacing: '1.5px', textTransform: 'uppercase' }}>
              Reliable Power & Trusted Security
            </div>
          </div>
        </Link>

        {/* DESKTOP NAV LINKS */}
        <div className="nav-links-desktop" style={{ display: 'flex', gap: '32px', alignItems: 'center' }}>
          {navLinks.map(({ label, path }) => (
            <Link key={path} to={path} style={linkStyle(location.pathname === path)}
              onMouseEnter={e => { if (location.pathname !== path) e.target.style.color = '#fff'; }}
              onMouseLeave={e => { if (location.pathname !== path) e.target.style.color = 'rgba(255,255,255,0.75)'; }}>
              {label}
            </Link>
          ))}
        </div>

        {/* DESKTOP CTA */}
        <button className="nav-cta-desktop" style={{
          background: GOLD, color: NAVY_DARK, fontSize: '13px', fontWeight: 600,
          padding: '9px 22px', borderRadius: '6px', cursor: 'pointer', border: 'none',
          letterSpacing: '0.3px', transition: 'background 0.2s',
        }}
          onMouseEnter={e => e.target.style.background = GOLD_LIGHT}
          onMouseLeave={e => e.target.style.background = GOLD}
          onClick={() => navigate('/contact')}>
          Get a Free Quote
        </button>

        {/* HAMBURGER (mobile only) */}
        <div className="hamburger" onClick={() => setMenuOpen(!menuOpen)}
          style={{ display: 'none', flexDirection: 'column', gap: '5px', cursor: 'pointer', padding: '4px' }}>
          <span style={{
            display: 'block', width: '24px', height: '2px', background: '#fff', borderRadius: '2px',
            transition: 'all 0.3s',
            transform: menuOpen ? 'translateY(7px) rotate(45deg)' : 'none',
          }} />
          <span style={{
            display: 'block', width: '24px', height: '2px', background: '#fff', borderRadius: '2px',
            transition: 'all 0.3s', opacity: menuOpen ? 0 : 1,
          }} />
          <span style={{
            display: 'block', width: '24px', height: '2px', background: '#fff', borderRadius: '2px',
            transition: 'all 0.3s',
            transform: menuOpen ? 'translateY(-7px) rotate(-45deg)' : 'none',
          }} />
        </div>
      </nav>

      {/* MOBILE MENU DRAWER */}
      {menuOpen && (
        <div style={{
          position: 'fixed', top: '70px', left: 0, right: 0, zIndex: 99,
          background: 'rgba(14,34,56,0.98)', backdropFilter: 'blur(12px)',
          borderBottom: '1px solid rgba(245,166,35,0.2)',
          padding: '16px 24px 24px',
          display: 'flex', flexDirection: 'column',
        }}>
          {navLinks.map(({ label, path }) => (
            <Link key={path} to={path}
              style={{
                fontSize: '16px', fontWeight: 500,
                color: location.pathname === path ? GOLD : 'rgba(255,255,255,0.8)',
                padding: '14px 0', borderBottom: '1px solid rgba(255,255,255,0.08)',
                textDecoration: 'none', display: 'block', transition: 'color 0.2s',
              }}
              onClick={() => setMenuOpen(false)}>
              {label}
            </Link>
          ))}
          <button style={{
            marginTop: '16px', background: GOLD, color: NAVY_DARK,
            fontSize: '14px', fontWeight: 600, padding: '13px 24px',
            borderRadius: '8px', cursor: 'pointer', border: 'none', width: '100%',
          }} onClick={() => { navigate('/contact'); setMenuOpen(false); }}>
            Get a Free Quote
          </button>
        </div>
      )}

      <style>{`
        @media (max-width: 1024px) {
          .nav-links-desktop { display: none !important; }
          .nav-cta-desktop { display: none !important; }
          .hamburger { display: flex !important; }
          nav { padding: 0 24px !important; }
        }
        @media (max-width: 480px) {
          nav { padding: 0 16px !important; }
        }
      `}</style>
    </>
  );
}
